// Football Products — shared presentational kit.
// Mirrors the Basketball _bbkit pattern: section taxonomy, icons, primitives
// (FpHead, FpHero, FpScoreboard, FpCard, FpAwait, FpTabs) and shared formatters.
// All styling under `.fp` scoped layer in styles/football-products.css.

import { type ReactNode, useState } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

// ── Formatters ───────────────────────────────────────────────
export const DASH = "—";
export function fmtOdds(o: number | string | null | undefined): string {
  if (typeof o === "string") return o.trim() || DASH;
  return typeof o === "number" && o > 0 ? o.toFixed(2) : DASH;
}
export function fmtPct(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? `${(n * 100).toFixed(0)}%` : DASH;
}
export function fmtNum(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("en-GH", { maximumFractionDigits: 0 }) : DASH;
}
export function fmtKick(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
// Date only (no time) — for the "match date" stat in the detail panel.
export function fmtMatchDate(iso: string | null | undefined): string {
  if (!iso) return "Date TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date TBD";
  return d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
}

// ── Country inference from league name ───────────────────────
// No country column exists on selections, so we derive it from the league/
// competition text. Best-effort: known leagues → {flag, name}; unknown → null
// (the UI simply omits country rather than guessing). Order matters — more
// specific patterns first (e.g. Brazil's "Série A" before Italy's "Serie A").
export interface LegCountry { flag: string; name: string }
const COUNTRY_RULES: Array<[RegExp, LegCountry]> = [
  [/\b(brasileir|brazil|brasil|serie a betano)\b/i, { flag: "🇧🇷", name: "Brazil" }],
  // Australia — its state NPLs say "Championship", so this MUST precede the
  // England rule (which matches a bare "championship").
  [/\b(a-?league|npl|australia|australian|tasmania|new south wales|nsw|victoria|queensland|capital territory|western australia|south australia)\b/i, { flag: "🇦🇺", name: "Australia" }],
  [/\b(chile|chilean)\b/i, { flag: "🇨🇱", name: "Chile" }],
  [/\b(veikkausliiga|ykk[oö]|finland|finnish)\b/i, { flag: "🇫🇮", name: "Finland" }],
  [/\b(superettan)\b/i, { flag: "🇸🇪", name: "Sweden" }],
  // ── Extended coverage (2026-06-13) so EVERY PowerBall/derivatives leg resolves
  // a flag. Order matters: these sit before the generic tier-word rules below
  // (e.g. China before the bare "super league" → Switzerland rule). ──
  [/\b(chinese|china|csl)\b/i, { flag: "🇨🇳", name: "China" }],
  [/\b(norra|norrland|svealand|g[öo]taland|division [12])\b/i, { flag: "🇸🇪", name: "Sweden" }],
  [/\b(eliteserien|toppserien|obos|3\.?\s*division|norway|norwegian)\b/i, { flag: "🇳🇴", name: "Norway" }],
  [/\b(a ?lyga|1 ?lyga|\blyga\b|lithuania|lithuanian)\b/i, { flag: "🇱🇹", name: "Lithuania" }],
  // No \b wrapper: "deild" is a suffix in "Úrvalsdeild" / "Meistaradeildin", so a
  // word boundary would miss it. Faroe precedes Iceland so Meistaradeildin wins.
  [/(meistaradeild|faroe|f[øo]royar)/i, { flag: "🇫🇴", name: "Faroe Islands" }],
  [/(úrvalsdeild|urvalsdeild|deild|iceland|icelandic)/i, { flag: "🇮🇸", name: "Iceland" }],
  [/\b(esiliiga|meistriliiga|estonia|estonian)\b/i, { flag: "🇪🇪", name: "Estonia" }],
  [/\b(virsl[íi]ga|virsliga|latvia|latvian)\b/i, { flag: "🇱🇻", name: "Latvia" }],
  [/\b(kakkonen)\b/i, { flag: "🇫🇮", name: "Finland" }],
  [/\b(erovnuli|georgia|georgian)\b/i, { flag: "🇬🇪", name: "Georgia" }],
  [/\b(azadegan|persian gulf|iran|iranian)\b/i, { flag: "🇮🇷", name: "Iran" }],
  [/\b(botola|morocc)\b/i, { flag: "🇲🇦", name: "Morocco" }],
  [/\b(ecuador|ecuadorian|liga pro)\b/i, { flag: "🇪🇨", name: "Ecuador" }],
  [/\b(intermedia|paraguay|paraguayan)\b/i, { flag: "🇵🇾", name: "Paraguay" }],
  [/\b(ligi kuu|tanzania|tanzanian)\b/i, { flag: "🇹🇿", name: "Tanzania" }],
  [/\b(iraqi?|iraq)\b/i, { flag: "🇮🇶", name: "Iraq" }],
  [/\b(league of ireland|airtricity|ireland|irish)\b/i, { flag: "🇮🇪", name: "Ireland" }],
  [/\b(liga i|romania|romanian)\b/i, { flag: "🇷🇴", name: "Romania" }],
  [/\b(k[\s-]?league|south korea|korea|korean)\b/i, { flag: "🇰🇷", name: "South Korea" }],
  [/\b(torneo federal|metropolitana|primera nacional|copa de la liga|argentin)\b/i, { flag: "🇦🇷", name: "Argentina" }],
  [/\b(libertadores|sudamericana|conmebol)\b/i, { flag: "🌎", name: "South America" }],
  [/\b(ga[úu]cha|gaucho|mineiro|carioca|paulista|copa do brasil)\b/i, { flag: "🇧🇷", name: "Brazil" }],
  [/\b(championnat national)\b/i, { flag: "🇫🇷", name: "France" }],
  [/\b(nba)\b/i, { flag: "🇺🇸", name: "USA" }],
  [/\b(premier league|championship|efl|league one|league two|fa cup|carabao|community shield|england|english)\b/i, { flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", name: "England" }],
  [/\b(la ?liga|laliga|segunda|copa del rey|spain|spanish)\b/i, { flag: "🇪🇸", name: "Spain" }],
  [/\b(serie [abc]|coppa italia|italy|italian)\b/i, { flag: "🇮🇹", name: "Italy" }],
  [/\b(austria|österreich|admiral)\b/i, { flag: "🇦🇹", name: "Austria" }],
  [/\b(bundesliga|dfb|germany|german)\b/i, { flag: "🇩🇪", name: "Germany" }],
  [/\b(ligue [12]|coupe de france|france|french)\b/i, { flag: "🇫🇷", name: "France" }],
  [/\b(eredivisie|eerste|knvb|netherlands|dutch|holland)\b/i, { flag: "🇳🇱", name: "Netherlands" }],
  [/\b(primeira|liga portugal|portugal|portuguese|taça)\b/i, { flag: "🇵🇹", name: "Portugal" }],
  [/\b(scottish|scotland|premiership)\b/i, { flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", name: "Scotland" }],
  [/\b(saudi|roshn)\b/i, { flag: "🇸🇦", name: "Saudi Arabia" }],
  [/\b(liga mx|mexico|mexican|liga bbva mx)\b/i, { flag: "🇲🇽", name: "Mexico" }],
  [/\b(mls|major league soccer|usa|united states|usl)\b/i, { flag: "🇺🇸", name: "USA" }],
  [/\b(liga profesional|primera|argentin)\b/i, { flag: "🇦🇷", name: "Argentina" }],
  [/\b(süper lig|super lig|turkey|turkish)\b/i, { flag: "🇹🇷", name: "Türkiye" }],
  [/\b(jupiler|pro league|belgium|belgian)\b/i, { flag: "🇧🇪", name: "Belgium" }],
  [/\b(eliteserien|norway|norwegian)\b/i, { flag: "🇳🇴", name: "Norway" }],
  [/\b(allsvenskan|sweden|swedish)\b/i, { flag: "🇸🇪", name: "Sweden" }],
  [/\b(superliga|denmark|danish)\b/i, { flag: "🇩🇰", name: "Denmark" }],
  [/\b(ekstraklasa|poland|polish)\b/i, { flag: "🇵🇱", name: "Poland" }],
  [/\b(super league greece|greece|greek)\b/i, { flag: "🇬🇷", name: "Greece" }],
  [/\b(super league|swiss|switzerland)\b/i, { flag: "🇨🇭", name: "Switzerland" }],
  [/\b(j1|j-league|j league|japan|japanese)\b/i, { flag: "🇯🇵", name: "Japan" }],
  [/\b(champions league|europa league|conference league|uefa|super cup)\b/i, { flag: "🇪🇺", name: "Europe" }],
  [/\b(world cup|nations league|friendl|qualif|copa america|euro 20|afcon|international)\b/i, { flag: "🌍", name: "International" }],
];
// The Odds API encodes country in its slug: soccer_<country>_<league>
// (e.g. soccer_chile_campeonato, soccer_finland_veikkausliiga). Map the token.
const SLUG_WORD_TO_ISO: Record<string, string> = {
  chile: "CL", finland: "FI", sweden: "SE", norway: "NO", denmark: "DK",
  brazil: "BR", spain: "ES", italy: "IT", germany: "DE", france: "FR",
  netherlands: "NL", portugal: "PT", japan: "JP", usa: "US", mexico: "MX",
  argentina: "AR", turkey: "TR", belgium: "BE", poland: "PL", greece: "GR",
  switzerland: "CH", austria: "AT", australia: "AU", england: "GB",
  scotland: "GB", korea: "KR", china: "CN", ireland: "IE", romania: "RO",
  estonia: "EE", colombia: "CO", uruguay: "UY", iraq: "IQ",
};
export function leagueCountry(league?: string | null, leagueShort?: string | null): LegCountry | null {
  const raw = `${league ?? ""} ${leagueShort ?? ""}`.trim();
  if (!raw) return null;
  // Underscore slugs ("soccer_fifa_world_cup") must read as words for the rules.
  const s = raw.replace(/_/g, " ");
  // Resolve explicit country names before ambiguous league labels.
  const named=s.toLowerCase().match(/\b(russia|austria|china|england|germany)\b/);
  const explicit:Record<string,string>={russia:"RU",austria:"AT",china:"CN",england:"GB",germany:"DE"};
  if(named)return countryFromCode(explicit[named[1]!]);
  if(/^(?:premier league|league two|league one|league 1|league 2|bundesliga)$/i.test(s.trim()))return null;
  for (const [re, c] of COUNTRY_RULES) if (re.test(s)) return c;
  // Fall back to the Odds API slug's country token.
  const m = raw.toLowerCase().match(/^soccer[_ ]([a-z]+)/);
  if (m && SLUG_WORD_TO_ISO[m[1]!]) return countryFromCode(SLUG_WORD_TO_ISO[m[1]!]);
  return null;
}

// Authoritative ISO-3166-1 alpha-2 → {flag, name}. The flag is derived from the
// code's regional-indicator codepoints (deterministic); the name covers the
// football nations we surface (falls back to the raw code if unmapped).
const ISO2_NAMES: Record<string, string> = {
  RU: "Russia", GB: "England", ES: "Spain", IT: "Italy", DE: "Germany", FR: "France",
  NL: "Netherlands", PT: "Portugal", BE: "Belgium", BR: "Brazil", AR: "Argentina",
  CL: "Chile", CO: "Colombia", MX: "Mexico", US: "USA", JP: "Japan",
  KR: "South Korea", CN: "China", SA: "Saudi Arabia", TR: "Türkiye", NO: "Norway",
  SE: "Sweden", DK: "Denmark", PL: "Poland", GR: "Greece", CH: "Switzerland",
  AT: "Austria", RO: "Romania", EE: "Estonia", FI: "Finland", IQ: "Iraq",
  IE: "Ireland", ZA: "South Africa", GH: "Ghana", AU: "Australia", UY: "Uruguay",
};
export function countryFromCode(code?: string | null): LegCountry | null {
  if (!code) return null;
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return null;
  const flag = String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
  return { flag, name: ISO2_NAMES[cc] ?? cc };
}

// ── Icons ────────────────────────────────────────────────────
export function FpIcon({ name, size = 16, color = "currentColor" }: { name: string; size?: number; color?: string }): JSX.Element {
  const c = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const p: Record<string, ReactNode> = {
    ball: (<><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18M5.5 7l13 10M5.5 17l13-10" /></>),
    bolt: (<><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></>),
    target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill={color} /></>),
    trophy: (<><path d="M6 4h12v4a6 6 0 0 1-12 0Z" /><path d="M6 6H3v2a3 3 0 0 0 3 3M18 6h3v2a3 3 0 0 1-3 3" /><path d="M10 14h4v3l2 3H8l2-3z" /></>),
    clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
    layers: (<><path d="m12 2 10 6.5v7L12 22 2 15.5v-7z" /><path d="M12 22v-7" /><path d="m22 9.5-10 6.5L2 9.5" /></>),
    check: <path d="M5 12l5 5L20 7" />,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    refresh: (<><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M21 3v5h-5M3 21v-5h5" /></>),
    info: (<><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>),
  };
  return <svg {...c}>{p[name] ?? null}</svg>;
}

// ── FpMark (gold football icon) ──────────────────────────────
export function FpMark({ size = 14 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 3v18M3 12h18M5.5 7l13 10M5.5 17l13-10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// ── FpHead (page header rail) ────────────────────────────────
export function FpHead({
  kicker, title, badge, status, live = false,
}: { kicker: string; title: string; badge?: string; status?: string; live?: boolean }): JSX.Element {
  return (
    <div className="fp-kicker-rail">
      <div className="fp-mark"><FpMark size={20} /></div>
      <div className="fp-title-stack">
        <div className="fp-kicker">
          <span>{kicker}</span>
          {badge && <span className="fp-badge">{badge}</span>}
        </div>
        <div className="fp-title">{title}</div>
      </div>
      {status && (
        <div className={`fp-status${live ? " live" : ""}`}>
          <span className="pulse" /><span>{status}</span>
        </div>
      )}
    </div>
  );
}

// ── FpHero ───────────────────────────────────────────────────
export function FpHero({
  kicker, title, lede, chips, aside,
}: { kicker: ReactNode; title: ReactNode; lede: string; chips?: ReactNode; aside?: ReactNode }): JSX.Element {
  return (
    <div className="fp-hero">
      <div>
        <div className="fp-hero-kicker">{kicker}</div>
        <h1>{title}</h1>
        <p className="lede">{lede}</p>
        {chips && <div className="fp-hero-chips">{chips}</div>}
      </div>
      {aside && <div className="fp-hero-aside">{aside}</div>}
    </div>
  );
}

export function HeroChip({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "accent" }): JSX.Element {
  return <span className={`fp-chip ${tone}`}><span className="d" />{children}</span>;
}

export function DefList({ rows }: { rows: Array<{ k: string; v: ReactNode; dim?: boolean }> }): JSX.Element {
  return (
    <div className="fp-deflist">
      {rows.map((r, i) => (
        <div key={i} className="fp-defrow">
          <div className="k">{r.k}</div>
          <div className={`v${r.dim ? " dim" : ""}`}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

// ── FpScoreboard ─────────────────────────────────────────────
export function FpScoreboard({ tiles }: { tiles: Array<{ label: string; value: ReactNode; sub?: string; live?: boolean }> }): JSX.Element {
  return (
    <div className="fp-scoreboard">
      {tiles.map((t, i) => (
        <div key={i} className={`fp-score${t.live ? " live" : ""}`}>
          <div className="top"><span className={`pip${t.live ? " on" : ""}`} /><span className="lab">{t.label}</span></div>
          <div className="big">{t.value}</div>
          {t.sub && <div className="sub">{t.sub}</div>}
          <div className="baseline" />
        </div>
      ))}
    </div>
  );
}

// ── FpCard ────────────────────────────────────────────────────
export function FpCard({
  kicker, title, action, children, padding = true,
}: { kicker?: string; title?: ReactNode; action?: ReactNode; children: ReactNode; padding?: boolean }): JSX.Element {
  return (
    <div className="fp-card">
      {(kicker || title || action) && (
        <div className="fp-card-head">
          <div>
            {kicker && <div className="fp-card-kicker">{kicker}</div>}
            {title && <div className="fp-card-title">{title}</div>}
          </div>
          {action && <div className="fp-card-action">{action}</div>}
        </div>
      )}
      <div className={padding ? "fp-card-body" : "fp-card-body-flush"}>{children}</div>
    </div>
  );
}

// ── FpTabs ───────────────────────────────────────────────────
export function FpTabs<T extends string>({
  tabs, active, onChange,
}: { tabs: Array<{ key: T; label: string; count?: number }>; active: T; onChange: (k: T) => void }): JSX.Element {
  return (
    <div className="fp-tabs">
      {tabs.map((t) => (
        <button key={t.key} className={`fp-tab${active === t.key ? " active" : ""}`} onClick={() => onChange(t.key)} type="button">
          {t.label}
          {t.count != null && <span className="fp-tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ── FpIndex (leg badge) ──────────────────────────────────────
export function FpIndex({ n, tone = "default" }: { n: number | string; tone?: "default" | "won" | "lost" | "accent" }): JSX.Element {
  return <div className={`fp-index ${tone}`}>{n}</div>;
}

// ── FpAwait ──────────────────────────────────────────────────
export function FpAwait({
  kicker = "CALIBRATING", title, body, ghosts = 5, icon = "clock",
}: { kicker?: string; title: string; body: string; ghosts?: number; icon?: string }): JSX.Element {
  return (
    <div className="fp-await">
      <div className="fp-await-mark"><FpIcon name={icon} size={24} /></div>
      <div className="fp-await-body">
        <div className="k">{kicker}</div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
      <div className="fp-await-ghosts">
        {Array.from({ length: ghosts }).map((_, i) => <div key={i} className="g" />)}
      </div>
    </div>
  );
}

// ── FpSeam ───────────────────────────────────────────────────
export function FpSeam(): JSX.Element {
  return (
    <div className="fp-seam">
      <svg width="100%" height="14" viewBox="0 0 1000 14" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 7 L 1000 7" fill="none" stroke="var(--border-faint)" strokeWidth="1" strokeDasharray="2 6" />
      </svg>
    </div>
  );
}

// ── FpSkeleton ───────────────────────────────────────────────
export function FpSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="fp-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="fp-skeleton-row" style={{ height: i === 0 ? 120 : i === 1 ? 180 : 100 }}>
          <div className="fp-shimmer" />
        </div>
      ))}
    </div>
  );
}

// ── FpDisclaimer ─────────────────────────────────────────────
export function FpDisclaimer({ text }: { text?: string }): JSX.Element {
  return (
    <div className="fp-disclaimer">
      <FpIcon name="info" size={14} />
      <span>{text ?? "Manual placement only. STRIKZA never executes, places, or settles any bet on your behalf — slips shown here are AI-assembled recommendations, not wagering transactions."}</span>
    </div>
  );
}

// ── FpLegRow (expandable with detail panel) ──────────────────
export function FpLegRow({
  index, fixture, league, market, selection, odds, prob, layer, tone = "default", kickoff,
  reasoning, fairPrice, edgePct, confidence, bestPlatform, bestOdds, clvGap, formScore,
  country, kickoffAt,
}: {
  index: number; fixture: string; league?: string; market: string; selection: string;
  odds: number; prob?: number; layer?: string; tone?: "default" | "won" | "lost"; kickoff?: string;
  reasoning?: string | null; fairPrice?: number | null; edgePct?: number | null;
  confidence?: number | string | null; bestPlatform?: string | null; bestOdds?: number | null;
  clvGap?: number | null; formScore?: number | null;
  country?: LegCountry | null; kickoffAt?: string | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasDetail = !!(reasoning || fairPrice || edgePct != null || confidence || country || kickoffAt);

  return (
    <div className={`fp-leg-wrap ${tone}${open ? " open" : ""}`}>
      <div className={`fp-leg-row ${tone}${hasDetail ? " clickable" : ""}`} onClick={hasDetail ? () => setOpen(!open) : undefined}>
        <FpIndex n={index} tone={tone === "won" ? "won" : tone === "lost" ? "lost" : "accent"} />
        <div className="fp-leg-info">
          <div className="fp-leg-fixture">{fixture}</div>
          <div className="fp-leg-meta">
            {country && (
              <span className="fp-leg-flag" title={country.name} aria-label={country.name}>
                {country.flag}
              </span>
            )}
            {league && <span className="lg">{league}</span>}
            {kickoff && <><span className="sep">·</span><span className="kick">{kickoff}</span></>}
          </div>
        </div>
        <div className="fp-leg-pick">
          <div className="fp-leg-pick-market">{market}</div>
          <div className="fp-leg-pick-sel">{selection}</div>
        </div>
        <div className="fp-leg-odds">
          <div className="odds">@{fmtOdds(odds)}</div>
          {prob != null && <div className="prob">{fmtPct(prob)}</div>}
        </div>
        {hasDetail && (
          <div className={`fp-leg-chev${open ? " open" : ""}`}>
            <FpIcon name="arrow" size={12} />
          </div>
        )}
      </div>

      {open && hasDetail && (
        <div className="fp-leg-detail">
          {/* Reasoning */}
          {reasoning && (
            <div className="fp-leg-reasoning">
              <div className="fp-leg-detail-label">AGENT REASONING</div>
              <p>{reasoning}</p>
            </div>
          )}

          {/* Stats grid */}
          <div className="fp-leg-stats">
            {(country || kickoffAt) && (
              <div className="fp-leg-stat">
                <div className="k">Country</div>
                <div className="v">{country ? `${country.flag} ${country.name}` : DASH}</div>
              </div>
            )}
            {kickoffAt && (
              <div className="fp-leg-stat tone-info">
                <div className="k">Kickoff</div>
                <div className="v">{fmtMatchDate(kickoffAt)} · {fmtKick(kickoffAt).split(" · ")[1] ?? ""}</div>
              </div>
            )}
            {prob != null && (
              <div className="fp-leg-stat tone-accent">
                <div className="k">Model Probability</div>
                <div className="v">{(prob * 100).toFixed(1)}%</div>
              </div>
            )}
            {layer && (
              <div className="fp-leg-stat tone-violet">
                <div className="k">Layer</div>
                <div className={`v layer-${layer}`}>{String(layer).replace(/_/g, " ")}</div>
              </div>
            )}
            {fairPrice != null && (
              <div className="fp-leg-stat tone-accent">
                <div className="k">Fair Price</div>
                <div className="v">@{fairPrice.toFixed(2)}</div>
              </div>
            )}
            {edgePct != null && (
              <div className={`fp-leg-stat ${edgePct > 0 ? "tone-pos" : edgePct < 0 ? "tone-neg" : ""}`}>
                <div className="k">Edge</div>
                <div className={`v ${edgePct > 0 ? "pos" : edgePct < 0 ? "neg" : ""}`}>
                  {edgePct > 0 ? "+" : ""}{(edgePct * 100).toFixed(1)}%
                </div>
              </div>
            )}
            {clvGap != null && (
              <div className={`fp-leg-stat ${clvGap > 0 ? "tone-pos" : ""}`}>
                <div className="k">CLV Gap</div>
                <div className={`v ${clvGap > 0 ? "pos" : ""}`}>
                  {clvGap > 0 ? "+" : ""}{(clvGap * 100).toFixed(1)}pp
                </div>
              </div>
            )}
            {confidence != null && (
              <div className="fp-leg-stat tone-info">
                <div className="k">Confidence</div>
                <div className="v">{typeof confidence === "number" ? `${confidence}/100` : confidence}</div>
              </div>
            )}
            {formScore != null && (
              <div className="fp-leg-stat tone-teal">
                <div className="k">Form</div>
                <div className="v">{formScore}/100</div>
              </div>
            )}
            {bestPlatform && bestOdds && (
              <div className="fp-leg-stat tone-pos">
                <div className="k">Best Price</div>
                <div className="v pos">@{bestOdds.toFixed(2)} on {bestPlatform}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hook ─────────────────────────────────────────────────────
export function useRpc<T>(fn: string): { loading: boolean; error: string | null; data: T | null } {
  const q = useRpcQuery<T | null>(fn);
  return { loading: q.isLoading, error: q.error ? q.error.message : null, data: q.data ?? null };
}
